import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  ArrowLeft, Layers, Plus, Pencil, Trash2, Megaphone, MessageSquare,
  CheckCircle2, XCircle, Send, AlertTriangle, Wallet, Users,
  BookOpen, Rocket, ExternalLink, Building2, Briefcase, Link2, Sparkles,
  ChevronDown, ChevronUp, X, Target, Globe, Calendar, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const STATUS_META = {
  draft:      { label: 'Draft',       cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  open:       { label: 'Open',        cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_funding: { label: 'In Funding',  cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  funded:     { label: 'Funded',      cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  active:     { label: 'Active',      cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed:  { label: 'Completed',   cls: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const STATUS_FLOW = ['draft', 'open', 'in_funding', 'funded', 'active', 'completed'];

// Mirror backend ALLOWED_TRANSITIONS so the UI only shows valid hops.
const ALLOWED_TRANSITIONS = {
  draft:      ['open'],
  open:       ['draft', 'in_funding'],
  in_funding: ['open', 'funded'],
  funded:     ['in_funding', 'active'],
  active:     ['completed'],
  completed:  [],
};

const CAPITAL_TYPES = [
  { value: 'public',        label: 'Public Capital' },
  { value: 'philanthropic', label: 'Philanthropic' },
  { value: 'private',       label: 'Private' },
  { value: 'blended',       label: 'Blended' },
];

const INSTRUMENTS = [
  'grant', 'loan', 'guarantee', 'equity', 'sib', 'fund',
  'subordinated_loan', 'first_loss_capital', 'other',
];

const RISK_LEVELS = [
  { value: 'low',    label: 'Low risk',    cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'medium', label: 'Medium risk', cls: 'bg-amber-100 text-amber-700' },
  { value: 'high',   label: 'High risk',   cls: 'bg-rose-100 text-rose-700' },
];

const SEGMENT_PHASES = [
  { value: 'initiating', label: 'Initiating' },
  { value: 'planning',   label: 'Planning' },
  { value: 'executing',  label: 'Executing' },
  { value: 'closing',    label: 'Closing' },
  { value: 'evaluation', label: 'Evaluation' },
];

const SDG_GOALS = [
  { n: 1,  label: '1. No Poverty' },
  { n: 2,  label: '2. Zero Hunger' },
  { n: 3,  label: '3. Good Health & Well-being' },
  { n: 4,  label: '4. Quality Education' },
  { n: 5,  label: '5. Gender Equality' },
  { n: 6,  label: '6. Clean Water & Sanitation' },
  { n: 7,  label: '7. Affordable & Clean Energy' },
  { n: 8,  label: '8. Decent Work & Economic Growth' },
  { n: 9,  label: '9. Industry, Innovation & Infrastructure' },
  { n: 10, label: '10. Reduced Inequalities' },
  { n: 11, label: '11. Sustainable Cities & Communities' },
  { n: 12, label: '12. Responsible Consumption & Production' },
  { n: 13, label: '13. Climate Action' },
  { n: 14, label: '14. Life Below Water' },
  { n: 15, label: '15. Life on Land' },
  { n: 16, label: '16. Peace, Justice & Strong Institutions' },
  { n: 17, label: '17. Partnerships for the Goals' },
];

const KPI_TYPES = ['quantitative', 'qualitative', 'impact'];
const STAKEHOLDER_TYPES = ['municipality', 'ngo', 'academia', 'private', 'beneficiary', 'donor', 'other'];
const FUNDING_SOURCE_TYPES = ['public', 'philanthropic', 'private', 'blended', 'in_kind'];

const EMPTY_SEG_FORM = {
  // Core
  title: '', description: '', financial_instrument: 'grant', capital_type: 'public',
  budget_allocation: '', risk_level: 'medium', risk_notes: '',
  timeline_start: '', timeline_end: '',
  // Extended
  sector: '', location: '', phase: 'planning',
  target_groups: [], target_beneficiary_count: '',
  expected_outcomes: [], sdg_alignment: [], theory_of_change: '',
  kpis: [],
  budget_breakdown: [], funding_sources: [],
  stakeholders: [],
  timeline_months: '', milestones: [],
  risks: [], assumptions: [],
};

const EUR = (v, c = 'EUR') => `${c} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB') : '—';

const BlendedPlanDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [plan, setPlan] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [actors, setActors] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('segments');

  // Dialogs
  const [segDialog, setSegDialog] = useState({ open: false, editing: null });
  const [segForm, setSegForm] = useState(EMPTY_SEG_FORM);
  const [segTab, setSegTab] = useState('basics');
  const [segDetailOpen, setSegDetailOpen] = useState({});  // segId -> bool (expand extended details on card)
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteSelected, setPromoteSelected] = useState([]);
  const [promoteMsg, setPromoteMsg] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [interestDialog, setInterestDialog] = useState({ open: false, segment: null });
  const [interestAmt, setInterestAmt] = useState('');
  const [interestNotes, setInterestNotes] = useState('');
  const [transitionDialog, setTransitionDialog] = useState({ open: false, target: null });
  const [transitionNote, setTransitionNote] = useState('');
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [linksDialog, setLinksDialog] = useState({ open: false });
  const [linksActorIds, setLinksActorIds] = useState([]);
  const [linksInvestorIds, setLinksInvestorIds] = useState([]);

  const role = (user?.role || '').toLowerCase();
  const isOwner = plan && (
    role === 'administrator' || role === 'admin' || role === 'project_manager' ||
    plan.creator_id === user?.id
  );
  const isInvestor = role === 'social_investor' || role === 'investor';
  // Admins / PMs / owners can transfer the plan into Projects or Social Investment Registry.
  const canPromote = plan && (
    role === 'administrator' || role === 'admin' || role === 'project_manager' ||
    plan.creator_id === user?.id
  );

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [planResp, invResp, actResp] = await Promise.all([
        axios.get(`${API}/api/blended-investments/${id}`, { headers }),
        axios.get(`${API}/api/social-investors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/social-actors`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPlan(planResp.data);
      setInvestors(Array.isArray(invResp.data) ? invResp.data : (invResp.data?.investors || []));
      setActors(Array.isArray(actResp.data) ? actResp.data : (actResp.data?.actors || actResp.data?.social_actors || []));
      try {
        const intResp = await axios.get(`${API}/api/blended-investments/${id}/interests`, { headers });
        setInterests(intResp.data || []);
      } catch (e) { setInterests([]); }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load plan');
      navigate('/combined-investments');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  const openSegmentDialog = (existing = null) => {
    if (existing) {
      setSegForm({
        title: existing.title || '',
        description: existing.description || '',
        financial_instrument: existing.financial_instrument || 'grant',
        capital_type: existing.capital_type || 'public',
        budget_allocation: String(existing.budget_allocation ?? ''),
        risk_level: existing.risk_level || 'medium',
        risk_notes: existing.risk_notes || '',
        timeline_start: existing.timeline_start || '',
        timeline_end: existing.timeline_end || '',
        sector: existing.sector || '',
        location: existing.location || '',
        phase: existing.phase || 'planning',
        target_groups: Array.isArray(existing.target_groups) ? existing.target_groups : [],
        target_beneficiary_count: existing.target_beneficiary_count != null ? String(existing.target_beneficiary_count) : '',
        expected_outcomes: Array.isArray(existing.expected_outcomes) ? existing.expected_outcomes : [],
        sdg_alignment: Array.isArray(existing.sdg_alignment) ? existing.sdg_alignment : [],
        theory_of_change: existing.theory_of_change || '',
        kpis: Array.isArray(existing.kpis) ? existing.kpis : [],
        budget_breakdown: Array.isArray(existing.budget_breakdown) ? existing.budget_breakdown : [],
        funding_sources: Array.isArray(existing.funding_sources) ? existing.funding_sources : [],
        stakeholders: Array.isArray(existing.stakeholders) ? existing.stakeholders : [],
        timeline_months: existing.timeline_months != null ? String(existing.timeline_months) : '',
        milestones: Array.isArray(existing.milestones) ? existing.milestones : [],
        risks: Array.isArray(existing.risks) ? existing.risks : [],
        assumptions: Array.isArray(existing.assumptions) ? existing.assumptions : [],
      });
    } else {
      setSegForm(EMPTY_SEG_FORM);
    }
    setSegTab('basics');
    setSegDialog({ open: true, editing: existing });
  };

  const submitSegment = async (e) => {
    e?.preventDefault?.();
    if (!segForm.title || !segForm.budget_allocation) {
      toast.error('Title and budget are required');
      return;
    }
    const body = {
      ...segForm,
      budget_allocation: parseFloat(segForm.budget_allocation),
      target_beneficiary_count: segForm.target_beneficiary_count === '' ? 0 : parseInt(segForm.target_beneficiary_count, 10) || 0,
      timeline_months: segForm.timeline_months === '' ? 0 : parseInt(segForm.timeline_months, 10) || 0,
      sdg_alignment: (segForm.sdg_alignment || []).map((n) => parseInt(n, 10)).filter((n) => !isNaN(n)),
    };
    try {
      if (segDialog.editing) {
        await axios.put(`${API}/api/blended-investments/${id}/segments/${segDialog.editing.id}`, body, { headers });
        toast.success('Segment updated');
      } else {
        await axios.post(`${API}/api/blended-investments/${id}/segments`, body, { headers });
        toast.success('Segment added');
      }
      setSegDialog({ open: false, editing: null });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save segment');
    }
  };

  const deleteSegment = async (seg) => {
    if (!window.confirm(`Delete segment "${seg.title}"?`)) return;
    try {
      await axios.delete(`${API}/api/blended-investments/${id}/segments/${seg.id}`, { headers });
      toast.success('Segment deleted');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const promote = async () => {
    try {
      await axios.post(`${API}/api/blended-investments/${id}/promote`,
        { investor_ids: promoteSelected, message: promoteMsg }, { headers });
      toast.success('Plan promoted to investors');
      setPromoteOpen(false);
      setPromoteSelected([]);
      setPromoteMsg('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to promote');
    }
  };

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const resp = await axios.get(
        `${API}/api/blended-investments/${id}/suggested-investors?limit=10`,
        { headers }
      );
      setSuggestions(resp.data?.suggested || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const inviteTopMatches = (count = 5) => {
    if (suggestions.length === 0) {
      toast.error('No suggestions yet — wait for the matcher to load.');
      return;
    }
    const top = suggestions.slice(0, count).map(s => s.investor_id);
    setPromoteSelected(top);
    toast.success(`Pre-selected top ${top.length} matching investor${top.length > 1 ? 's' : ''}.`);
  };

  const transition = async () => {
    try {
      await axios.post(`${API}/api/blended-investments/${id}/transition`,
        { to_status: transitionDialog.target, note: transitionNote }, { headers });
      toast.success(`Status changed to ${transitionDialog.target}`);
      setTransitionDialog({ open: false, target: null });
      setTransitionNote('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transition failed');
    }
  };

  const expressInterest = async () => {
    const amt = parseFloat(interestAmt);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(
        `${API}/api/blended-investments/${id}/segments/${interestDialog.segment.id}/interest`,
        { amount: amt, notes: interestNotes }, { headers }
      );
      toast.success('Interest submitted');
      setInterestDialog({ open: false, segment: null });
      setInterestAmt(''); setInterestNotes('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit interest');
    }
  };

  const decideInterest = async (interest, decision) => {
    const note = decision === 'rejected'
      ? (window.prompt('Reason for rejection (optional)') || '')
      : '';
    try {
      await axios.post(
        `${API}/api/blended-investments/${id}/interests/${interest.id}/decision`,
        { decision, note }, { headers }
      );
      toast.success(`Interest ${decision}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const submitFeedback = async (e) => {
    e?.preventDefault?.();
    if (!feedbackMsg.trim()) return;
    try {
      await axios.post(`${API}/api/blended-investments/${id}/feedback`,
        { message: feedbackMsg.trim() }, { headers });
      setFeedbackMsg('');
      fetchAll();
    } catch (err) {
      toast.error('Failed to post feedback');
    }
  };

  const convertToProject = async () => {
    if (!window.confirm(
      'Transfer this plan into the Projects Module?\n\n' +
      'This will:\n' +
      '• Create a new project pre-filled from the plan (ToC, SDGs, sector, location)\n' +
      '• Add each segment as a budget category\n' +
      '• Seed every segment KPI into the Project KPI register\n' +
      '• Seed every target group as a Project beneficiary\n' +
      '• Seed every segment risk and stakeholder into the Project\n' +
      '• Add each accepted investor as a team member (affiliation: investor)\n' +
      '• Add each linked Social Actor as a team member (affiliation: social_actor)\n' +
      (plan?.status === 'funded' ? '• Auto-transition the plan to "Active"\n' : '') +
      '\nYou can still edit everything afterwards in the Projects module.'
    )) return;
    try {
      const resp = await axios.post(`${API}/api/blended-investments/${id}/convert-to-project`, {}, { headers });
      const d = resp.data || {};
      toast.success(
        `Project created — ${d.budget_categories_created || 0} budget categories, ` +
        `${d.kpis_created || 0} KPIs, ${d.beneficiaries_created || 0} beneficiaries, ` +
        `${d.risks_created || 0} risks, ${d.stakeholders_created || 0} stakeholders, ` +
        `${d.team_members_created || 0} team members.`
      );
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Conversion failed');
    }
  };

  const registerAsInvestment = async () => {
    if (!window.confirm(
      'Register this plan in the Social Investment Registry?\n\n' +
      'This will:\n' +
      '• Create a new entry in the Social Investment Registry pre-filled from this plan\n' +
      '• Map each segment as a budget breakdown line and funding source\n' +
      '• Aggregate target groups, expected outcomes, stakeholders and SDGs across all segments\n' +
      '• Keep this Blended Investment intact — both records stay linked\n\n' +
      'You can edit the registry entry afterwards in the Social Investment Registration module.'
    )) return;
    try {
      const resp = await axios.post(
        `${API}/api/blended-investments/${id}/register-as-investment`, {}, { headers }
      );
      const d = resp.data || {};
      toast.success(
        `Registered in Social Investment Registry — ${d.segments_mapped || 0} segments, ` +
        `${d.target_groups_mapped || 0} target groups, ${d.expected_outcomes_mapped || 0} outcomes, ` +
        `${d.stakeholders_mapped || 0} stakeholders.`
      );
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    }
  };

  const openLinksDialog = () => {
    setLinksActorIds(plan?.social_actor_ids || []);
    setLinksInvestorIds(plan?.social_investor_ids || []);
    setLinksDialog({ open: true });
  };

  const submitLinks = async () => {
    try {
      await axios.patch(`${API}/api/blended-investments/${id}/links`,
        { social_actor_ids: linksActorIds, social_investor_ids: linksInvestorIds },
        { headers });
      toast.success('Registry links updated');
      setLinksDialog({ open: false });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const allocPct = useMemo(() => {
    if (!plan || !plan.total_budget) return 0;
    return Math.round((plan.allocated_budget / plan.total_budget) * 100);
  }, [plan]);

  if (loading || !plan) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const status = STATUS_META[plan.status] || { label: plan.status, cls: 'bg-slate-100' };
  const currentStepIdx = STATUS_FLOW.indexOf(plan.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/combined-investments')} data-testid="back-to-list-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="plan-title">
                <Layers className="w-6 h-6 text-indigo-600" /> {plan.title}
              </h1>
              <p className="text-sm text-slate-700 mt-0.5">{plan.investment_title}</p>
            </div>
          </div>
          <Badge className={`${status.cls} border text-sm px-3 py-1`} data-testid="plan-status-badge">{status.label}</Badge>
        </div>

        {/* Status workflow stepper */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 overflow-x-auto">
              {STATUS_FLOW.map((s, idx) => {
                const meta = STATUS_META[s];
                const reached = idx <= currentStepIdx;
                return (
                  <React.Fragment key={s}>
                    <div className={`flex flex-col items-center min-w-[80px] px-2 py-1 rounded ${reached ? meta.cls : 'bg-slate-50 text-slate-700'}`}>
                      <span className="text-xs font-medium">{meta.label}</span>
                    </div>
                    {idx < STATUS_FLOW.length - 1 && <div className={`flex-1 h-0.5 ${idx < currentStepIdx ? 'bg-indigo-300' : 'bg-slate-200'} min-w-[20px]`} />}
                  </React.Fragment>
                );
              })}
            </div>
            {isOwner && (
              <div className="flex flex-wrap gap-2 mt-3">
                {(ALLOWED_TRANSITIONS[plan.status] || []).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="outline"
                    onClick={() => setTransitionDialog({ open: true, target: s })}
                    data-testid={`transition-to-${s}`}
                  >
                    → {STATUS_META[s].label}
                  </Button>
                ))}
                {(ALLOWED_TRANSITIONS[plan.status] || []).length === 0 && (
                  <p className="text-xs text-slate-700 italic">Terminal state — no further transitions.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="tile-budget"><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-700">Total budget</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{EUR(plan.total_budget, plan.currency)}</p>
          </CardContent></Card>
          <Card data-testid="tile-allocated"><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-700">Allocated</p>
            <p className="text-xl font-bold text-emerald-700 mt-1">{EUR(plan.allocated_budget, plan.currency)}</p>
            <p className="text-xs text-slate-700 mt-1">{allocPct}% of total</p>
          </CardContent></Card>
          <Card data-testid="tile-committed"><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-700">Committed</p>
            <p className="text-xl font-bold text-indigo-700 mt-1">{EUR(plan.committed_amount, plan.currency)}</p>
          </CardContent></Card>
          <Card data-testid="tile-segments"><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-700">Segments</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{plan.segment_count}</p>
          </CardContent></Card>
        </div>

        {/* Promote button */}
        {isOwner && ['draft', 'open', 'in_funding'].includes(plan.status) && (
          <div className="mb-4">
            <Button onClick={() => { setPromoteOpen(true); fetchSuggestions(); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="promote-btn">
              <Megaphone className="w-4 h-4 mr-2" /> Promote to investors
            </Button>
            {plan.promoted_at && (
              <span className="text-xs text-slate-700 ml-3">
                Last promoted {fmtDate(plan.promoted_at)} by {plan.promoted_by} · invited {plan.invited_investor_ids?.length || 'all investors'}
              </span>
            )}
          </div>
        )}

        {/* Convert-to-Project CTA — for advanced-stage plans not yet linked to a project */}
        {canPromote && ['funded', 'active', 'completed'].includes(plan.status) && !plan.linked_project_id && (
          <Card className="mb-4 border-emerald-300 bg-emerald-50/50" data-testid="convert-cta">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-emerald-900 flex items-center gap-2">
                  <Rocket className="w-4 h-4" /> Transfer to Projects Module
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  This plan is in <span className="font-medium">{STATUS_META[plan.status]?.label}</span> status.
                  Transfer it into the Projects Module — segments become budget categories, investors and
                  social actors become team members.
                </p>
              </div>
              <Button onClick={convertToProject} className="bg-emerald-700 hover:bg-emerald-800 whitespace-nowrap" data-testid="convert-btn">
                <Rocket className="w-4 h-4 mr-2" /> Transfer to Projects
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Register-as-Social-Investment CTA — for early-stage plans not yet linked to a registry entry */}
        {canPromote && ['draft', 'open', 'in_funding'].includes(plan.status) && !plan.linked_si_investment_id && (
          <Card className="mb-4 border-sky-300 bg-sky-50/50" data-testid="register-si-cta">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-sky-900 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> Register in Social Investment Registry
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  This plan is in <span className="font-medium">{STATUS_META[plan.status]?.label}</span> status.
                  Transfer it to the Social Investment Registration module so it appears as a registered
                  social investment idea — segments become budget lines and funding sources.
                </p>
              </div>
              <Button onClick={registerAsInvestment} className="bg-sky-700 hover:bg-sky-800 whitespace-nowrap" data-testid="register-si-btn">
                <BookOpen className="w-4 h-4 mr-2" /> Register in Registry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Linked-project banner — once converted */}
        {plan.linked_project_id && (
          <Card className="mb-4 border-indigo-200 bg-indigo-50/50" data-testid="linked-project-banner">
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-700 flex items-center gap-2">
                <Rocket className="w-4 h-4 text-indigo-600" />
                <span>This plan was promoted to a Project on {fmtDate(plan.linked_project_at)}{plan.linked_project_by ? ` by ${plan.linked_project_by}` : ''}.</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${plan.linked_project_id}`)}
                data-testid="goto-linked-project"
              >
                <ExternalLink className="w-3 h-3 mr-1" /> Open Project
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Linked Social Investment banner — once registered */}
        {plan.linked_si_investment_id && (
          <Card className="mb-4 border-sky-200 bg-sky-50/50" data-testid="linked-si-banner">
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-700 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-sky-700" />
                <span>This plan is registered in the Social Investment Registry on {fmtDate(plan.linked_si_investment_at)}{plan.linked_si_investment_by ? ` by ${plan.linked_si_investment_by}` : ''}.</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/social-investment-registration?open=${plan.linked_si_investment_id}`)}
                data-testid="goto-linked-si"
              >
                <ExternalLink className="w-3 h-3 mr-1" /> Open in Registry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SI-origin banner — when this plan was created from a Social Investment Registry idea */}
        {plan.si_investment_id && (
          <Card className="mb-4 border-purple-200 bg-purple-50/50" data-testid="si-origin-banner">
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-700 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-600" />
                <span>
                  Originates from Social Investment{plan.si_investment_title ? `: ${plan.si_investment_title}` : ''}.
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/social-investment-registration?open=${plan.si_investment_id}`)}
                data-testid="goto-si-origin"
              >
                <ExternalLink className="w-3 h-3 mr-1" /> Open Original Idea
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="segments" data-testid="tab-segments"><Layers className="w-4 h-4 mr-2" /> Segments</TabsTrigger>
            <TabsTrigger value="interests" data-testid="tab-interests"><Wallet className="w-4 h-4 mr-2" /> Investor Interest ({interests.length})</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback"><MessageSquare className="w-4 h-4 mr-2" /> Feedback ({plan.feedback?.length || 0})</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview"><BookOpen className="w-4 h-4 mr-2" /> Overview</TabsTrigger>
          </TabsList>

          {/* Segments tab */}
          <TabsContent value="segments" className="space-y-3">
            {isOwner && (
              <Button onClick={() => openSegmentDialog(null)} variant="outline" data-testid="add-segment-btn">
                <Plus className="w-4 h-4 mr-2" /> Add segment
              </Button>
            )}
            {(!plan.segments || plan.segments.length === 0) ? (
              <Card><CardContent className="py-12 text-center text-slate-700">
                <Layers className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                No segments yet. {isOwner ? 'Break this investment into discrete funding parts.' : ''}
              </CardContent></Card>
            ) : (
              plan.segments.map((seg) => {
                const segPct = seg.budget_allocation > 0
                  ? Math.round((seg.committed_amount / seg.budget_allocation) * 100) : 0;
                const risk = RISK_LEVELS.find((r) => r.value === seg.risk_level) || RISK_LEVELS[1];
                return (
                  <Card key={seg.id} data-testid={`segment-${seg.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-slate-900">{seg.title}</h3>
                            <Badge variant="outline" className="text-xs capitalize">{seg.capital_type}</Badge>
                            <Badge variant="outline" className="text-xs capitalize">{seg.financial_instrument.replace(/_/g, ' ')}</Badge>
                            <Badge className={`${risk.cls} text-xs border-0`}><AlertTriangle className="w-3 h-3 mr-1" />{risk.label}</Badge>
                            <Badge className={`${STATUS_META[seg.status]?.cls || 'bg-slate-100'} border text-xs`}>{seg.status}</Badge>
                          </div>
                          {seg.description && <p className="text-sm text-slate-600 mt-1">{seg.description}</p>}
                          {seg.risk_notes && <p className="text-xs text-amber-700 mt-1 italic">Risk: {seg.risk_notes}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          {isInvestor && ['open', 'in_funding'].includes(plan.status) && (
                            <Button
                              size="sm"
                              className="bg-emerald-700 hover:bg-emerald-800"
                              onClick={() => setInterestDialog({ open: true, segment: seg })}
                              data-testid={`interest-${seg.id}`}
                            >
                              <Send className="w-3 h-3 mr-1" /> I'm interested
                            </Button>
                          )}
                          {isOwner && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openSegmentDialog(seg)} aria-label={`Edit segment ${seg.title}`} data-testid={`edit-seg-${seg.id}`}>
                                <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteSegment(seg)} className="text-rose-700" aria-label={`Delete segment ${seg.title}`} data-testid={`del-seg-${seg.id}`}>
                                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-700 mb-1">
                          <span>{EUR(seg.committed_amount, seg.currency)} committed of {EUR(seg.budget_allocation, seg.currency)}</span>
                          <span>{segPct}%</span>
                        </div>
                        <Progress value={segPct} className="h-2" />
                        {seg.pending_interest_amount > 0 && (
                          <p className="text-xs text-amber-600 mt-1">+ {EUR(seg.pending_interest_amount, seg.currency)} pending interest</p>
                        )}
                      </div>

                      {/* Quick-glance extended meta */}
                      {(seg.phase || seg.sector || seg.location || (seg.sdg_alignment || []).length > 0 || seg.target_beneficiary_count > 0) && (
                        <div className="flex flex-wrap gap-2 mt-3 text-xs">
                          {seg.phase && <Badge variant="outline" className="capitalize"><Rocket className="w-3 h-3 mr-1" />{seg.phase}</Badge>}
                          {seg.sector && <Badge variant="outline"><Briefcase className="w-3 h-3 mr-1" />{seg.sector}</Badge>}
                          {seg.location && <Badge variant="outline"><Globe className="w-3 h-3 mr-1" />{seg.location}</Badge>}
                          {seg.target_beneficiary_count > 0 && <Badge variant="outline"><Users className="w-3 h-3 mr-1" />{seg.target_beneficiary_count} beneficiaries</Badge>}
                          {(seg.sdg_alignment || []).length > 0 && <Badge variant="outline"><Target className="w-3 h-3 mr-1" />SDGs: {seg.sdg_alignment.join(', ')}</Badge>}
                          {(seg.timeline_start || seg.timeline_end || seg.timeline_months > 0) && (
                            <Badge variant="outline">
                              <Calendar className="w-3 h-3 mr-1" />
                              {seg.timeline_start ? fmtDate(seg.timeline_start) : '—'}
                              {(seg.timeline_end || seg.timeline_months > 0) ? ' → ' : ''}
                              {seg.timeline_end ? fmtDate(seg.timeline_end) : (seg.timeline_months ? `${seg.timeline_months}mo` : '')}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Details toggle */}
                      {(
                        (seg.target_groups || []).length > 0 ||
                        (seg.expected_outcomes || []).length > 0 ||
                        (seg.kpis || []).length > 0 ||
                        (seg.budget_breakdown || []).length > 0 ||
                        (seg.funding_sources || []).length > 0 ||
                        (seg.stakeholders || []).length > 0 ||
                        (seg.milestones || []).length > 0 ||
                        (seg.risks || []).length > 0 ||
                        (seg.assumptions || []).length > 0 ||
                        seg.theory_of_change
                      ) && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <Button
                            type="button" size="sm" variant="ghost"
                            className="text-xs text-slate-600 hover:text-slate-900 h-7"
                            onClick={() => setSegDetailOpen((o) => ({ ...o, [seg.id]: !o[seg.id] }))}
                            data-testid={`toggle-seg-detail-${seg.id}`}
                          >
                            {segDetailOpen[seg.id] ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                            {segDetailOpen[seg.id] ? 'Hide full analysis' : 'Show full analysis'}
                          </Button>

                          {segDetailOpen[seg.id] && (
                            <div className="mt-2 space-y-3 text-xs">
                              {seg.theory_of_change && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Theory of Change</p>
                                  <p className="text-slate-600 whitespace-pre-wrap">{seg.theory_of_change}</p>
                                </div>
                              )}

                              {(seg.target_groups || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Target groups</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.target_groups.map((g, i) => (
                                      <li key={i}>
                                        <span className="font-medium">{g.name || 'Unnamed'}</span>
                                        {g.count > 0 && <span> · {g.count} people</span>}
                                        {g.is_vulnerable && <Badge className="ml-1 text-xs bg-rose-100 text-rose-700 border-0">vulnerable</Badge>}
                                        {g.description && <span className="italic"> — {g.description}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.expected_outcomes || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Expected outcomes</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.expected_outcomes.map((o, i) => (
                                      <li key={i}>
                                        <span className="font-medium">{o.title || 'Outcome'}</span>
                                        {o.indicator && <span> · indicator: {o.indicator}</span>}
                                        {o.target && <span> · target: {o.target}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.kpis || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">KPIs</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.kpis.map((k, i) => (
                                      <li key={i}>
                                        <span className="font-medium">{k.name || 'KPI'}</span>
                                        {k.type && <span className="italic"> ({k.type})</span>}
                                        {' · '}baseline {k.baseline || '—'} → target {k.target || '—'}{k.unit ? ` ${k.unit}` : ''}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.budget_breakdown || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Budget breakdown</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.budget_breakdown.map((b, i) => (
                                      <li key={i}>{b.category || 'Line'}: {EUR(b.amount, seg.currency)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.funding_sources || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Funding sources</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.funding_sources.map((f, i) => (
                                      <li key={i}>{f.source || 'Source'}: {EUR(f.amount, seg.currency)} <Badge variant="outline" className="text-xs ml-1 capitalize">{f.type}</Badge></li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.stakeholders || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Stakeholders</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.stakeholders.map((s, i) => (
                                      <li key={i}>
                                        <span className="font-medium">{s.name || 'Unnamed'}</span>
                                        {s.role && <span> — {s.role}</span>}
                                        {s.type && <Badge variant="outline" className="text-xs ml-1 capitalize">{s.type}</Badge>}
                                        {s.contact && <span className="italic"> · {s.contact}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.milestones || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Milestones</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.milestones.map((m, i) => (
                                      <li key={i}>
                                        <span className="font-medium">{m.title || 'Milestone'}</span>
                                        {m.date && <span> · {fmtDate(m.date)}</span>}
                                        {m.notes && <span className="italic"> — {m.notes}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.risks || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Detailed risks</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.risks.map((r, i) => (
                                      <li key={i}>
                                        {r.description || 'Risk'}
                                        {r.probability && <span className="italic"> · prob: {r.probability}</span>}
                                        {r.impact && <span className="italic"> · impact: {r.impact}</span>}
                                        {r.mitigation && <span> → mitigation: {r.mitigation}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(seg.assumptions || []).length > 0 && (
                                <div>
                                  <p className="font-semibold text-slate-700 mb-1">Assumptions</p>
                                  <ul className="list-disc pl-5 text-slate-600">
                                    {seg.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Interests tab */}
          <TabsContent value="interests">
            {interests.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-700">
                <Wallet className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                No investor interest yet.
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {interests.map((it) => {
                  const seg = plan.segments.find((s) => s.id === it.segment_id);
                  const statusCls = it.status === 'accepted' ? 'bg-emerald-100 text-emerald-700'
                    : it.status === 'rejected' ? 'bg-rose-100 text-rose-700'
                    : it.status === 'withdrawn' ? 'bg-slate-100 text-slate-600'
                    : 'bg-amber-100 text-amber-700';
                  return (
                    <Card key={it.id} data-testid={`interest-row-${it.id}`}>
                      <CardContent className="p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Users className="w-4 h-4 text-slate-700" />
                            <span className="font-medium text-slate-900">{it.investor_username}</span>
                            <Badge className={`${statusCls} border-0 text-xs`}>{it.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5">
                            {EUR(it.amount, it.currency)} for segment <span className="italic">"{seg?.title || it.segment_id}"</span>
                          </p>
                          {it.notes && <p className="text-xs text-slate-700 mt-0.5">"{it.notes}"</p>}
                          {it.decision_note && <p className="text-xs text-slate-700 mt-0.5">Decision note: {it.decision_note}</p>}
                          <p className="text-xs text-slate-700 mt-0.5">{fmtDate(it.created_at)}</p>
                        </div>
                        {isOwner && it.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                              onClick={() => decideInterest(it, 'accepted')} data-testid={`accept-${it.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-700 border-rose-300"
                              onClick={() => decideInterest(it, 'rejected')} data-testid={`reject-${it.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Feedback tab */}
          <TabsContent value="feedback">
            <Card>
              <CardContent className="p-4">
                <form onSubmit={submitFeedback} className="flex gap-2 mb-4">
                  <label htmlFor="feedback-input-el" className="sr-only">Feedback or comment</label>
                  <Input
                    id="feedback-input-el"
                    aria-label="Feedback or comment"
                    placeholder="Post feedback or a comment to discuss segmentation…"
                    value={feedbackMsg}
                    onChange={(e) => setFeedbackMsg(e.target.value)}
                    data-testid="feedback-input"
                  />
                  <Button type="submit" disabled={!feedbackMsg.trim()} aria-label="Send feedback" data-testid="feedback-send">
                    <Send className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </form>
                {(plan.feedback || []).length === 0 ? (
                  <p className="text-sm text-slate-700 italic text-center py-6">No feedback yet. Use this thread to refine segmentation based on stakeholder input — segmentation is a dynamic process.</p>
                ) : (
                  <div className="space-y-2">
                    {plan.feedback.map((m) => (
                      <div key={m.id} className="border-l-2 border-indigo-300 pl-3 py-1" data-testid={`feedback-${m.id}`}>
                        <p className="text-xs text-slate-700">
                          <span className="font-medium text-slate-700">{m.author_username}</span>
                          <span className="ml-2 text-slate-700">({m.author_role})</span>
                          <span className="ml-2 text-slate-700">{fmtDate(m.created_at)}</span>
                        </p>
                        <p className="text-sm text-slate-700 mt-0.5">{m.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Overview tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Linked registries */}
            <Card data-testid="linked-registries-card">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-indigo-600" /> Linked Registries
                </CardTitle>
                {isOwner && (
                  <Button size="sm" variant="outline" onClick={openLinksDialog} data-testid="edit-links-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit links
                  </Button>
                )}
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-700 mb-2 flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" /> Social Actors ({plan.linked_social_actors?.length || 0})
                  </p>
                  {(plan.linked_social_actors || []).length === 0 ? (
                    <p className="text-sm text-slate-700 italic">No social actors linked yet.</p>
                  ) : (
                    <ul className="space-y-1" data-testid="linked-actors-list">
                      {plan.linked_social_actors.map((a) => (
                        <li key={a.id} className="text-sm flex items-center gap-2 border border-slate-100 rounded p-2 hover:border-emerald-200">
                          <Building2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">{a.organization_name || a.name}</p>
                            <p className="text-xs text-slate-700">{a.actor_type || 'Social actor'}{a.city ? ` · ${a.city}` : ''}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-700 mb-2 flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5" /> Social Investors ({plan.linked_social_investors?.length || 0})
                  </p>
                  {(plan.linked_social_investors || []).length === 0 ? (
                    <p className="text-sm text-slate-700 italic">No social investors linked yet.</p>
                  ) : (
                    <ul className="space-y-1" data-testid="linked-investors-list">
                      {plan.linked_social_investors.map((i) => (
                        <li key={i.id} className="text-sm flex items-center gap-2 border border-slate-100 rounded p-2 hover:border-indigo-200">
                          <Briefcase className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">{i.organization_name}</p>
                            <p className="text-xs text-slate-700">{(i.investor_type || '').replace(/_/g, ' ')}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Plan Overview</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-700">Description</Label>
                  <p className="text-slate-700 mt-1">{plan.description || <span className="italic text-slate-700">—</span>}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-700">Theory of Change</Label>
                  <p className="text-slate-700 mt-1 whitespace-pre-wrap">{plan.theory_of_change || <span className="italic text-slate-700">—</span>}</p>
                </div>
                {plan.promoted_message && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-700">Promotion message</Label>
                    <p className="text-slate-700 mt-1 italic">"{plan.promoted_message}"</p>
                  </div>
                )}
                {plan.status_history?.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-700">Status history</Label>
                    <ul className="text-xs text-slate-600 mt-1 space-y-1">
                      {plan.status_history.map((h, i) => (
                        <li key={i}>
                          <span className="text-slate-700">{fmtDate(h.at)}</span> · {h.from} → <strong>{h.to}</strong>
                          <span className="text-slate-700"> by {h.by}</span>
                          {h.note && <span className="italic text-slate-700"> — {h.note}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Segment Dialog */}
      <Dialog open={segDialog.open} onOpenChange={(o) => setSegDialog({ ...segDialog, open: o })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="segment-dialog">
          <DialogHeader>
            <DialogTitle>{segDialog.editing ? 'Edit segment' : 'Add segment'}</DialogTitle>
            <DialogDescription>
              A segment is a discrete part of the investment that can be funded independently. Capture full analysis (scope, outcomes, KPIs, financials, stakeholders, timeline, risks) so it can later convert seamlessly into a Project or Social Investment entry.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSegment} className="space-y-3">
            <Tabs value={segTab} onValueChange={setSegTab}>
              <TabsList className="w-full flex-wrap h-auto gap-1">
                <TabsTrigger value="basics" data-testid="seg-tab-basics">Basics</TabsTrigger>
                <TabsTrigger value="scope" data-testid="seg-tab-scope">Scope & Beneficiaries</TabsTrigger>
                <TabsTrigger value="outcomes" data-testid="seg-tab-outcomes">Outcomes & KPIs</TabsTrigger>
                <TabsTrigger value="financial" data-testid="seg-tab-financial">Financial Plan</TabsTrigger>
                <TabsTrigger value="stakeholders" data-testid="seg-tab-stakeholders">Stakeholders</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="seg-tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="risks" data-testid="seg-tab-risks">Risks</TabsTrigger>
              </TabsList>

              {/* ── Basics ── */}
              <TabsContent value="basics" className="space-y-3 pt-3">
                <div><Label>Title *</Label><Input aria-label="Title" data-testid="seg-title" value={segForm.title} onChange={(e) => setSegForm({ ...segForm, title: e.target.value })} required /></div>
                <div><Label>Description</Label><Textarea aria-label="Description" rows={2} value={segForm.description} onChange={(e) => setSegForm({ ...segForm, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Capital type *</Label>
                    <Select value={segForm.capital_type} onValueChange={(v) => setSegForm({ ...segForm, capital_type: v })}>
                      <SelectTrigger aria-label="Capital type" data-testid="seg-capital-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{CAPITAL_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Financial instrument *</Label>
                    <Select value={segForm.financial_instrument} onValueChange={(v) => setSegForm({ ...segForm, financial_instrument: v })}>
                      <SelectTrigger aria-label="Financial instrument" data-testid="seg-instrument"><SelectValue /></SelectTrigger>
                      <SelectContent>{INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Budget allocation *</Label><Input aria-label="Budget allocation" data-testid="seg-budget" type="number" min="0.01" step="0.01" value={segForm.budget_allocation} onChange={(e) => setSegForm({ ...segForm, budget_allocation: e.target.value })} required /></div>
                  <div>
                    <Label>Implementation phase</Label>
                    <Select value={segForm.phase} onValueChange={(v) => setSegForm({ ...segForm, phase: v })}>
                      <SelectTrigger aria-label="Implementation phase" data-testid="seg-phase"><SelectValue /></SelectTrigger>
                      <SelectContent>{SEGMENT_PHASES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* ── Scope & Beneficiaries ── */}
              <TabsContent value="scope" className="space-y-3 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Sector</Label><Input aria-label="Sector" data-testid="seg-sector" value={segForm.sector} onChange={(e) => setSegForm({ ...segForm, sector: e.target.value })} placeholder="e.g., Education, Health, Environment…" /></div>
                  <div><Label>Location</Label><Input aria-label="Location" data-testid="seg-location" value={segForm.location} onChange={(e) => setSegForm({ ...segForm, location: e.target.value })} placeholder="City / Region" /></div>
                </div>
                <div><Label>Target beneficiary count</Label><Input aria-label="Target beneficiary count" data-testid="seg-beneficiary-count" type="number" min="0" value={segForm.target_beneficiary_count} onChange={(e) => setSegForm({ ...segForm, target_beneficiary_count: e.target.value })} /></div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Target groups</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, target_groups: [...segForm.target_groups, { name: '', count: 0, description: '', is_vulnerable: false }] })} data-testid="add-target-group">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add group
                    </Button>
                  </div>
                  {segForm.target_groups.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No target groups yet.</p>
                  ) : segForm.target_groups.map((tg, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-start">
                      <Input className="col-span-3" placeholder="Group name" value={tg.name || ''} onChange={(e) => { const arr = [...segForm.target_groups]; arr[i] = { ...arr[i], name: e.target.value }; setSegForm({ ...segForm, target_groups: arr }); }} />
                      <Input className="col-span-2" type="number" min="0" placeholder="Count" value={tg.count || ''} onChange={(e) => { const arr = [...segForm.target_groups]; arr[i] = { ...arr[i], count: parseInt(e.target.value, 10) || 0 }; setSegForm({ ...segForm, target_groups: arr }); }} />
                      <Input className="col-span-5" placeholder="Description" value={tg.description || ''} onChange={(e) => { const arr = [...segForm.target_groups]; arr[i] = { ...arr[i], description: e.target.value }; setSegForm({ ...segForm, target_groups: arr }); }} />
                      <label className="col-span-1 flex items-center gap-1 text-xs pt-2">
                        <Checkbox aria-label="Vulnerable group" checked={!!tg.is_vulnerable} onCheckedChange={(v) => { const arr = [...segForm.target_groups]; arr[i] = { ...arr[i], is_vulnerable: !!v }; setSegForm({ ...segForm, target_groups: arr }); }} />
                        <span>Vuln.</span>
                      </label>
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, target_groups: segForm.target_groups.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Outcomes & KPIs ── */}
              <TabsContent value="outcomes" className="space-y-3 pt-3">
                <div>
                  <Label>Theory of Change</Label><Textarea aria-label="Theory of Change" rows={3} value={segForm.theory_of_change} onChange={(e) => setSegForm({ ...segForm, theory_of_change: e.target.value })} placeholder="How inputs and activities lead to outcomes and long-term impact for this segment…" data-testid="seg-toc" />
                </div>
                <div>
                  <Label>SDG Alignment</Label>
                  <div className="grid grid-cols-3 gap-1 mt-1 max-h-40 overflow-y-auto border rounded p-2">
                    {SDG_GOALS.map((s) => (
                      <label key={s.n} className="flex items-center gap-1 text-xs">
                        <Checkbox
                          aria-label={`SDG ${s.n}: ${s.label}`}
                          data-testid={`seg-sdg-${s.n}`}
                          checked={segForm.sdg_alignment.includes(s.n)}
                          onCheckedChange={(v) => {
                            const set = new Set(segForm.sdg_alignment);
                            if (v) set.add(s.n); else set.delete(s.n);
                            setSegForm({ ...segForm, sdg_alignment: Array.from(set).sort((a, b) => a - b) });
                          }}
                        />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Expected Outcomes</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, expected_outcomes: [...segForm.expected_outcomes, { title: '', indicator: '', target: '' }] })} data-testid="add-outcome">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add outcome
                    </Button>
                  </div>
                  {segForm.expected_outcomes.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No outcomes yet.</p>
                  ) : segForm.expected_outcomes.map((o, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-4" placeholder="Outcome" value={o.title || ''} onChange={(e) => { const arr = [...segForm.expected_outcomes]; arr[i] = { ...arr[i], title: e.target.value }; setSegForm({ ...segForm, expected_outcomes: arr }); }} />
                      <Input className="col-span-4" placeholder="Indicator" value={o.indicator || ''} onChange={(e) => { const arr = [...segForm.expected_outcomes]; arr[i] = { ...arr[i], indicator: e.target.value }; setSegForm({ ...segForm, expected_outcomes: arr }); }} />
                      <Input className="col-span-3" placeholder="Target value" value={o.target || ''} onChange={(e) => { const arr = [...segForm.expected_outcomes]; arr[i] = { ...arr[i], target: e.target.value }; setSegForm({ ...segForm, expected_outcomes: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, expected_outcomes: segForm.expected_outcomes.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>KPIs</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, kpis: [...segForm.kpis, { name: '', type: 'quantitative', unit: '', baseline: '', target: '' }] })} data-testid="add-kpi">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add KPI
                    </Button>
                  </div>
                  {segForm.kpis.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No KPIs yet.</p>
                  ) : segForm.kpis.map((k, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-3" placeholder="KPI name" value={k.name || ''} onChange={(e) => { const arr = [...segForm.kpis]; arr[i] = { ...arr[i], name: e.target.value }; setSegForm({ ...segForm, kpis: arr }); }} />
                      <Select value={k.type || 'quantitative'} onValueChange={(v) => { const arr = [...segForm.kpis]; arr[i] = { ...arr[i], type: v }; setSegForm({ ...segForm, kpis: arr }); }}>
                        <SelectTrigger aria-label="KPI type" className="col-span-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{KPI_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-2" placeholder="Unit" value={k.unit || ''} onChange={(e) => { const arr = [...segForm.kpis]; arr[i] = { ...arr[i], unit: e.target.value }; setSegForm({ ...segForm, kpis: arr }); }} />
                      <Input className="col-span-2" placeholder="Baseline" value={k.baseline || ''} onChange={(e) => { const arr = [...segForm.kpis]; arr[i] = { ...arr[i], baseline: e.target.value }; setSegForm({ ...segForm, kpis: arr }); }} />
                      <Input className="col-span-2" placeholder="Target" value={k.target || ''} onChange={(e) => { const arr = [...segForm.kpis]; arr[i] = { ...arr[i], target: e.target.value }; setSegForm({ ...segForm, kpis: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, kpis: segForm.kpis.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Financial Plan ── */}
              <TabsContent value="financial" className="space-y-3 pt-3">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Budget breakdown</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, budget_breakdown: [...segForm.budget_breakdown, { category: '', amount: 0 }] })} data-testid="add-budget-line">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add line
                    </Button>
                  </div>
                  {segForm.budget_breakdown.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No breakdown yet.</p>
                  ) : segForm.budget_breakdown.map((b, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-8" placeholder="Category (e.g., Personnel, Equipment)" value={b.category || ''} onChange={(e) => { const arr = [...segForm.budget_breakdown]; arr[i] = { ...arr[i], category: e.target.value }; setSegForm({ ...segForm, budget_breakdown: arr }); }} />
                      <Input className="col-span-3" type="number" min="0" step="0.01" placeholder="Amount" value={b.amount || ''} onChange={(e) => { const arr = [...segForm.budget_breakdown]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 }; setSegForm({ ...segForm, budget_breakdown: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, budget_breakdown: segForm.budget_breakdown.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Funding sources</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, funding_sources: [...segForm.funding_sources, { source: '', amount: 0, type: 'public' }] })} data-testid="add-funding-source">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add source
                    </Button>
                  </div>
                  {segForm.funding_sources.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No funding sources yet.</p>
                  ) : segForm.funding_sources.map((f, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-5" placeholder="Source name" value={f.source || ''} onChange={(e) => { const arr = [...segForm.funding_sources]; arr[i] = { ...arr[i], source: e.target.value }; setSegForm({ ...segForm, funding_sources: arr }); }} />
                      <Input className="col-span-3" type="number" min="0" step="0.01" placeholder="Amount" value={f.amount || ''} onChange={(e) => { const arr = [...segForm.funding_sources]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 }; setSegForm({ ...segForm, funding_sources: arr }); }} />
                      <Select value={f.type || 'public'} onValueChange={(v) => { const arr = [...segForm.funding_sources]; arr[i] = { ...arr[i], type: v }; setSegForm({ ...segForm, funding_sources: arr }); }}>
                        <SelectTrigger aria-label="Funding source type" className="col-span-3"><SelectValue /></SelectTrigger>
                        <SelectContent>{FUNDING_SOURCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, funding_sources: segForm.funding_sources.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Stakeholders ── */}
              <TabsContent value="stakeholders" className="space-y-3 pt-3">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Stakeholders</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, stakeholders: [...segForm.stakeholders, { name: '', role: '', type: 'other', contact: '' }] })} data-testid="add-stakeholder">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add stakeholder
                    </Button>
                  </div>
                  {segForm.stakeholders.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No stakeholders yet.</p>
                  ) : segForm.stakeholders.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-3" placeholder="Name" value={s.name || ''} onChange={(e) => { const arr = [...segForm.stakeholders]; arr[i] = { ...arr[i], name: e.target.value }; setSegForm({ ...segForm, stakeholders: arr }); }} />
                      <Input className="col-span-3" placeholder="Role" value={s.role || ''} onChange={(e) => { const arr = [...segForm.stakeholders]; arr[i] = { ...arr[i], role: e.target.value }; setSegForm({ ...segForm, stakeholders: arr }); }} />
                      <Select value={s.type || 'other'} onValueChange={(v) => { const arr = [...segForm.stakeholders]; arr[i] = { ...arr[i], type: v }; setSegForm({ ...segForm, stakeholders: arr }); }}>
                        <SelectTrigger aria-label="Stakeholder type" className="col-span-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAKEHOLDER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-3" placeholder="Contact (email/phone)" value={s.contact || ''} onChange={(e) => { const arr = [...segForm.stakeholders]; arr[i] = { ...arr[i], contact: e.target.value }; setSegForm({ ...segForm, stakeholders: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, stakeholders: segForm.stakeholders.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Timeline ── */}
              <TabsContent value="timeline" className="space-y-3 pt-3">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Timeline start</Label><Input aria-label="Timeline start" type="date" value={segForm.timeline_start} onChange={(e) => setSegForm({ ...segForm, timeline_start: e.target.value })} /></div>
                  <div><Label>Timeline end</Label><Input aria-label="Timeline end" type="date" value={segForm.timeline_end} onChange={(e) => setSegForm({ ...segForm, timeline_end: e.target.value })} /></div>
                  <div><Label>Duration (months)</Label><Input aria-label="Duration (months)" type="number" min="0" value={segForm.timeline_months} onChange={(e) => setSegForm({ ...segForm, timeline_months: e.target.value })} data-testid="seg-months" /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Milestones</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, milestones: [...segForm.milestones, { title: '', date: '', notes: '' }] })} data-testid="add-milestone">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add milestone
                    </Button>
                  </div>
                  {segForm.milestones.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No milestones yet.</p>
                  ) : segForm.milestones.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-4" placeholder="Milestone title" value={m.title || ''} onChange={(e) => { const arr = [...segForm.milestones]; arr[i] = { ...arr[i], title: e.target.value }; setSegForm({ ...segForm, milestones: arr }); }} />
                      <Input className="col-span-3" type="date" value={m.date || ''} onChange={(e) => { const arr = [...segForm.milestones]; arr[i] = { ...arr[i], date: e.target.value }; setSegForm({ ...segForm, milestones: arr }); }} />
                      <Input className="col-span-4" placeholder="Notes" value={m.notes || ''} onChange={(e) => { const arr = [...segForm.milestones]; arr[i] = { ...arr[i], notes: e.target.value }; setSegForm({ ...segForm, milestones: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, milestones: segForm.milestones.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── Risks ── */}
              <TabsContent value="risks" className="space-y-3 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Risk level</Label>
                    <Select value={segForm.risk_level} onValueChange={(v) => setSegForm({ ...segForm, risk_level: v })}>
                      <SelectTrigger aria-label="Risk level" data-testid="seg-risk"><SelectValue /></SelectTrigger>
                      <SelectContent>{RISK_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Risk notes (summary)</Label><Input aria-label="Risk notes (summary)" value={segForm.risk_notes} onChange={(e) => setSegForm({ ...segForm, risk_notes: e.target.value })} placeholder="e.g., FX risk, regulatory uncertainty…" /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Detailed risks</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, risks: [...segForm.risks, { description: '', probability: 'medium', impact: 'medium', mitigation: '' }] })} data-testid="add-risk">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add risk
                    </Button>
                  </div>
                  {segForm.risks.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No detailed risks yet.</p>
                  ) : segForm.risks.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                      <Input className="col-span-4" placeholder="Risk description" value={r.description || ''} onChange={(e) => { const arr = [...segForm.risks]; arr[i] = { ...arr[i], description: e.target.value }; setSegForm({ ...segForm, risks: arr }); }} />
                      <Select value={r.probability || 'medium'} onValueChange={(v) => { const arr = [...segForm.risks]; arr[i] = { ...arr[i], probability: v }; setSegForm({ ...segForm, risks: arr }); }}>
                        <SelectTrigger aria-label="Probability" className="col-span-2"><SelectValue placeholder="Prob." /></SelectTrigger>
                        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                      </Select>
                      <Select value={r.impact || 'medium'} onValueChange={(v) => { const arr = [...segForm.risks]; arr[i] = { ...arr[i], impact: v }; setSegForm({ ...segForm, risks: arr }); }}>
                        <SelectTrigger aria-label="Impact" className="col-span-2"><SelectValue placeholder="Impact" /></SelectTrigger>
                        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                      </Select>
                      <Input className="col-span-3" placeholder="Mitigation" value={r.mitigation || ''} onChange={(e) => { const arr = [...segForm.risks]; arr[i] = { ...arr[i], mitigation: e.target.value }; setSegForm({ ...segForm, risks: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-700" onClick={() => setSegForm({ ...segForm, risks: segForm.risks.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Assumptions</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setSegForm({ ...segForm, assumptions: [...segForm.assumptions, ''] })} data-testid="add-assumption">
                      <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add assumption
                    </Button>
                  </div>
                  {segForm.assumptions.length === 0 ? (
                    <p className="text-xs text-slate-700 italic">No assumptions yet.</p>
                  ) : segForm.assumptions.map((a, i) => (
                    <div key={i} className="flex gap-2 mt-2">
                      <Input className="flex-1" placeholder="Assumption" value={a} onChange={(e) => { const arr = [...segForm.assumptions]; arr[i] = e.target.value; setSegForm({ ...segForm, assumptions: arr }); }} />
                      <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => setSegForm({ ...segForm, assumptions: segForm.assumptions.filter((_, j) => j !== i) })}>
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setSegDialog({ open: false, editing: null })}>Cancel</Button>
              <Button type="submit" data-testid="seg-submit">{segDialog.editing ? 'Save changes' : 'Add segment'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Promote Dialog */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="max-w-2xl" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle>Promote plan to investors</DialogTitle>
            <DialogDescription>
              Invite specific social investors to consider this plan, or leave the list empty to open it to all investors. The plan will auto-transition to "Open".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Message (optional)</Label><Textarea aria-label="Message (optional)" rows={2} value={promoteMsg} onChange={(e) => setPromoteMsg(e.target.value)} placeholder="A short note investors will see…" data-testid="promote-msg" />
            </div>

            {/* AI-suggested top matches */}
            <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3" data-testid="suggested-investors-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-teal-700" />
                  <span className="font-medium text-sm text-teal-900">Suggested top matches</span>
                  {loadingSuggestions && (
                    <span className="text-xs text-teal-700 italic">computing…</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => inviteTopMatches(3)}
                    disabled={suggestions.length === 0}
                    data-testid="invite-top-3-btn"
                    className="text-xs h-7"
                  >
                    Invite top 3
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => inviteTopMatches(5)}
                    disabled={suggestions.length === 0}
                    data-testid="invite-top-5-btn"
                    className="text-xs h-7"
                  >
                    Invite top 5
                  </Button>
                </div>
              </div>
              {!loadingSuggestions && suggestions.length === 0 && (
                <p className="text-xs text-slate-700 italic">No matching investors yet — they may need approval first.</p>
              )}
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {suggestions.slice(0, 10).map((s) => {
                  const target = s.investor_id;
                  const isSelected = promoteSelected.includes(target);
                  const qCls = s.match_quality === 'Excellent Match' ? 'bg-emerald-100 text-emerald-700'
                            : s.match_quality === 'Good Match' ? 'bg-sky-100 text-sky-700'
                            : s.match_quality === 'Moderate Match' ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600';
                  return (
                    <label
                      key={s.investor_id}
                      className={`flex items-center gap-2 text-sm p-1.5 rounded cursor-pointer ${isSelected ? 'bg-white border border-teal-300' : 'hover:bg-white/60'}`}
                      data-testid={`suggestion-row-${s.investor_id}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        aria-label={`Select ${s.organization_name}`}
                        onCheckedChange={(c) => {
                          setPromoteSelected((prev) =>
                            c ? [...prev, target] : prev.filter((x) => x !== target));
                        }}
                      />
                      <span className="flex-1 truncate">{s.organization_name}</span>
                      {!s.budget_compatible && (
                        <Badge variant="outline" className="text-xs text-rose-600 border-rose-200">budget&nbsp;mismatch</Badge>
                      )}
                      <Badge className={`${qCls} border-0 text-xs`}>{s.match_score}</Badge>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>All investors ({promoteSelected.length} selected)</Label>
              <div className="border rounded max-h-48 overflow-y-auto p-2 space-y-1">
                {investors.length === 0 ? (
                  <p className="text-sm text-slate-700 italic p-2">No investors registered yet — leaving empty will open to all when more register.</p>
                ) : investors.map((inv) => (
                  <label key={inv.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox
                      checked={promoteSelected.includes(inv.id)}
                      aria-label={`Invite ${inv.organization_name || inv.username || inv.id}`}
                      onCheckedChange={(c) => {
                        const target = inv.id;
                        setPromoteSelected((prev) =>
                          c ? [...prev, target] : prev.filter((x) => x !== target));
                      }}
                      data-testid={`invite-${inv.id}`}
                    />
                    <span>{inv.organization_name || inv.username || inv.id}</span>
                    {inv.investor_type && <span className="text-xs text-slate-700">({inv.investor_type})</span>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-700 mt-1">Tip: leave empty to open to all investors.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteOpen(false)}>Cancel</Button>
            <Button onClick={promote} className="bg-indigo-600 hover:bg-indigo-700" data-testid="promote-confirm">
              <Megaphone className="w-4 h-4 mr-2" /> Promote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interest dialog */}
      <Dialog open={interestDialog.open} onOpenChange={(o) => setInterestDialog({ ...interestDialog, open: o })}>
        <DialogContent className="max-w-md" data-testid="interest-dialog">
          <DialogHeader>
            <DialogTitle>Express interest</DialogTitle>
            <DialogDescription>
              {interestDialog.segment && <>Segment: <strong>{interestDialog.segment.title}</strong> · Allocation: {EUR(interestDialog.segment.budget_allocation, interestDialog.segment.currency)} · Currently committed: {EUR(interestDialog.segment.committed_amount, interestDialog.segment.currency)}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount you'd commit</Label><Input aria-label="Amount you'd commit" data-testid="interest-amount" type="number" min="0.01" step="0.01" value={interestAmt} onChange={(e) => setInterestAmt(e.target.value)} /></div>
            <div><Label>Notes (optional)</Label><Textarea aria-label="Notes (optional)" rows={2} value={interestNotes} onChange={(e) => setInterestNotes(e.target.value)} placeholder="Conditions, terms, or comments…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInterestDialog({ open: false, segment: null })}>Cancel</Button>
            <Button onClick={expressInterest} className="bg-emerald-700 hover:bg-emerald-800" data-testid="interest-submit">Submit interest</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transition dialog */}
      <Dialog open={transitionDialog.open} onOpenChange={(o) => setTransitionDialog({ ...transitionDialog, open: o })}>
        <DialogContent className="max-w-md" data-testid="transition-dialog">
          <DialogHeader>
            <DialogTitle>Move to {STATUS_META[transitionDialog.target]?.label}</DialogTitle>
            <DialogDescription>
              Transitions follow the workflow rules. Add a note explaining why (optional but recommended).
            </DialogDescription>
          </DialogHeader>
          <Textarea aria-label="Transition reason or context" rows={3} value={transitionNote} onChange={(e) => setTransitionNote(e.target.value)} placeholder="Reason / context…" data-testid="transition-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransitionDialog({ open: false, target: null })}>Cancel</Button>
            <Button onClick={transition} data-testid="transition-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Links Dialog */}
      <Dialog open={linksDialog.open} onOpenChange={(o) => setLinksDialog({ open: o })}>
        <DialogContent className="max-w-2xl" data-testid="links-dialog">
          <DialogHeader>
            <DialogTitle>Edit linked registries</DialogTitle>
            <DialogDescription>
              Link this blended investment to one or more Social Actors (the implementing organisations) and one or more Social Investors (the funding side). When the plan is converted to a project, every linked actor & investor will be auto-added as a team member.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1 mb-1"><Building2 className="w-3.5 h-3.5 text-emerald-700" /> Social Actors ({linksActorIds.length})</Label>
              <div className="border rounded max-h-64 overflow-y-auto p-2 space-y-1" data-testid="links-actors-pool">
                {actors.length === 0 ? (
                  <p className="text-sm text-slate-700 italic p-2">No social actors registered yet.</p>
                ) : actors.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox
                      checked={linksActorIds.includes(a.id)}
                      aria-label={`Link social actor ${a.organization_name || a.name}`}
                      onCheckedChange={(c) =>
                        setLinksActorIds((prev) => c ? [...prev, a.id] : prev.filter((x) => x !== a.id))
                      }
                      data-testid={`link-actor-${a.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-800 truncate block">{a.organization_name || a.name}</span>
                      <span className="text-xs text-slate-700">{a.actor_type || 'Social actor'}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-1"><Briefcase className="w-3.5 h-3.5 text-indigo-600" /> Social Investors ({linksInvestorIds.length})</Label>
              <div className="border rounded max-h-64 overflow-y-auto p-2 space-y-1" data-testid="links-investors-pool">
                {investors.length === 0 ? (
                  <p className="text-sm text-slate-700 italic p-2">No social investors registered yet.</p>
                ) : investors.map((i) => (
                  <label key={i.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox
                      checked={linksInvestorIds.includes(i.id)}
                      aria-label={`Link social investor ${i.organization_name}`}
                      onCheckedChange={(c) =>
                        setLinksInvestorIds((prev) => c ? [...prev, i.id] : prev.filter((x) => x !== i.id))
                      }
                      data-testid={`link-investor-${i.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-800 truncate block">{i.organization_name}</span>
                      <span className="text-xs text-slate-700">{(i.investor_type || '').replace(/_/g, ' ')}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinksDialog({ open: false })}>Cancel</Button>
            <Button onClick={submitLinks} className="bg-indigo-600 hover:bg-indigo-700" data-testid="links-save">
              Save links
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlendedPlanDetail;
